import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, CircularProgress, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Button, MenuItem, Divider, FormControlLabel, Switch
} from '@mui/material';
import { ArrowBack, Add, Edit, Delete, MenuBook, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const API = process.env.REACT_APP_API_URL;

const CATEGORIES = ['Languages', 'Mathematics', 'Sciences', 'Humanities', 'Arts & Crafts', 'Technical', 'Physical Education', 'Religious Education', 'Other'];

const emptyForm = { areaCode: '', areaName: '', category: '', description: '', preferredSession: 'Any', isActive: true };
const SESSIONS = ['Any', 'Morning', 'Mid-morning', 'Afternoon'];

const InfoRow = ({ label, value }) => (
  <Box display="flex" gap={1} mb={0.5}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 500 }}>{label}</Typography>
    <Typography variant="body2">{value || <em style={{ color: '#bbb' }}>—</em>}</Typography>
  </Box>
);

const LearningAreaList = () => {
  const navigate = useNavigate();
  const [areas,    setAreas]    = useState([]);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');
  const [loading,  setLoading]  = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [form,     setForm]     = useState(emptyForm);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: dialogOpen, value: form, onClose: () => setDialogOpen(false) });
  const [saving,   setSaving]   = useState(false);
  const [viewArea, setViewArea] = useState(null);
  const [confirmDlg, setConfirmDlg] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchAreas = async () => {
    try {
      const { data } = await axios.get(`${API}/api/academics/learning-areas`, { headers });
      setAreas(data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load learning areas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAreas(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 2500);
    return () => clearTimeout(t);
  }, [success]);

  const handleCreate = () => {
    setEditingCode(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const handleEdit = (a) => {
    setEditingCode(a.areaCode);
    setForm({ areaCode: a.areaCode, areaName: a.areaName, category: a.category || '', description: a.description || '', preferredSession: a.preferredSession || 'Any', isActive: a.isActive !== false });
    setDialogOpen(true);
  };

  const handleDelete = (code) => setConfirmDlg(code);

  const confirmDelete = async () => {
    const code = confirmDlg;
    setConfirmDlg(null);
    try {
      await axios.delete(`${API}/api/academics/learning-areas/${code}`, { headers });
      setSuccess(`${code} deleted`);
      fetchAreas();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  const handleSave = async () => {
    if (!form.areaCode.trim()) { setError('Area Code is required'); return; }
    if (!form.areaName.trim()) { setError('Area Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      if (editingCode) {
        await axios.put(`${API}/api/academics/learning-areas/${editingCode}`, form, { headers });
        setSuccess(`${editingCode} updated`);
      } else {
        await axios.post(`${API}/api/academics/learning-areas`, form, { headers });
        setSuccess(`${form.areaCode} created`);
      }
      setDialogOpen(false);
      fetchAreas();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading...</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <MenuBook color="primary" />
              <Box>
                <Typography variant="h5" color="primary">Learning Areas</Typography>
                <Typography variant="body2" color="text.secondary">
                  Master subject list · Linked to stages and assessments
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap={1}>
              <IconButton onClick={() => navigate('/academics')} title="Back to Academics">
                <ArrowBack />
              </IconButton>
              <IconButton onClick={handleCreate} title="New Learning Area"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Add />
              </IconButton>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          {areas.length === 0 ? (
            <Typography color="text.secondary">No learning areas yet. Click + to add subjects.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Code</strong></TableCell>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Category</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {areas.map(a => (
                  <TableRow key={a.areaCode} hover sx={{ cursor: 'pointer' }}
                    onClick={() => setViewArea(a)}>
                    <TableCell><strong>{a.areaCode}</strong></TableCell>
                    <TableCell>{a.areaName}</TableCell>
                    <TableCell>
                      {a.category && (
                        <Chip label={a.category} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{a.description}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={a.isActive !== false ? 'Active' : 'Inactive'}
                        color={a.isActive !== false ? 'success' : 'default'}
                        size="small" variant="outlined"
                      />
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <IconButton size="small" color="primary" onClick={() => handleEdit(a)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(a.areaCode)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── View card ── */}
      <Dialog open={Boolean(viewArea)} onClose={() => setViewArea(null)} maxWidth="xs" fullWidth>
        {viewArea && (
          <>
            <DialogTitle component="div">
              <Box display="flex" alignItems="center" gap={1}>
                <MenuBook color="primary" />
                <Box>
                  <Typography variant="h6" lineHeight={1}>{viewArea.areaName}</Typography>
                  <Typography variant="caption" color="text.secondary">{viewArea.areaCode}</Typography>
                </Box>
                <Box ml="auto">
                  <Chip
                    label={viewArea.isActive !== false ? 'Active' : 'Inactive'}
                    color={viewArea.isActive !== false ? 'success' : 'default'}
                    size="small" variant="outlined"
                  />
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <InfoRow label="Code"        value={viewArea.areaCode} />
              <InfoRow label="Name"        value={viewArea.areaName} />
              <InfoRow label="Category"    value={viewArea.category} />
              <InfoRow label="Session"     value={viewArea.preferredSession && viewArea.preferredSession !== 'Any' ? `${viewArea.preferredSession} only` : 'Any time'} />
              <Divider sx={{ my: 1 }} />
              <InfoRow label="Description" value={viewArea.description} />
            </DialogContent>
            <DialogActions>
              <Button startIcon={<Edit />} onClick={() => { setViewArea(null); handleEdit(viewArea); }}>Edit</Button>
              <Button variant="outlined" onClick={() => setViewArea(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Confirm Delete dialog ── */}
      <Dialog open={Boolean(confirmDlg)} onClose={() => setConfirmDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>Delete <strong>{confirmDlg}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── Create / Edit dialog ── */}
      {discardDialog}
      <Dialog open={dialogOpen} onClose={attemptClose} maxWidth="xs" fullWidth>
        <DialogTitle>{editingCode ? `Edit — ${editingCode}` : 'New Learning Area'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Area Code *" value={form.areaCode}
              onChange={e => setForm(f => ({ ...f, areaCode: e.target.value }))}
              disabled={!!editingCode}
              size="small" fullWidth placeholder="e.g. MATH, ENG, SCI"
              helperText={editingCode ? 'Code cannot be changed' : ''}
            />
            <TextField
              label="Area Name *" value={form.areaName}
              onChange={e => setForm(f => ({ ...f, areaName: e.target.value }))}
              size="small" fullWidth placeholder="e.g. Mathematics"
            />
            <TextField
              select label="Category" value={form.category}
              onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              size="small" fullWidth>
              <MenuItem value=""><em>— None —</em></MenuItem>
              {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <TextField
              select label="Preferred timetable session" value={form.preferredSession}
              onChange={e => setForm(f => ({ ...f, preferredSession: e.target.value }))}
              size="small" fullWidth
              helperText="Morning-only keeps e.g. Maths/sciences out of the afternoon on the timetable">
              {SESSIONS.map(s => <MenuItem key={s} value={s}>{s === 'Any' ? 'Any time' : `${s} only`}</MenuItem>)}
            </TextField>
            <TextField
              label="Description" value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              size="small" fullWidth multiline rows={2}
            />
            {editingCode && (
              <FormControlLabel
                control={<Switch checked={form.isActive}
                  onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />}
                label="Active"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LearningAreaList;
